import { styled } from "@mui/material/styles";
import { Box, ButtonBase, Card, Avatar, Chip, Grid, Stack, Typography } from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { APP_COLORS } from "../../../enums";

const COMPACT_ACTION_MIN_HEIGHT = 44;
const STACKED_ACTION_MIN_HEIGHT = 56;
const COMPACT_ICON_SIZE = 20;
const STACKED_ICON_SIZE = 16;

export const ProjectCardActionIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== "compact",
})<{ compact?: boolean }>(({ compact }) => {
  const size = compact ? COMPACT_ICON_SIZE : STACKED_ICON_SIZE;

  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    lineHeight: 0,
    color: "inherit",
    "& svg": {
      width: size,
      height: size,
    },
  };
});

export const ProjectCardActionLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "compact",
})<{ compact?: boolean }>(({ theme, compact }) => ({
  fontWeight: 600,
  color: "inherit",
  textAlign: "center",
  lineHeight: 1.2,
  paddingLeft: theme.spacing(0.25),
  paddingRight: theme.spacing(0.25),
  ...(compact
    ? {
      whiteSpace: "nowrap",
      fontSize: theme.typography.body2.fontSize,
    }
    : {
      fontSize: 10,
      [theme.breakpoints.up("sm")]: {
        fontSize: 11,
      },
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    }),
}));

export const ProjectCardActionButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== "compact" && prop !== "confirmed",
})<{ compact?: boolean; confirmed?: boolean }>(
  ({ theme, compact, confirmed }) => ({
    flex: 1,
    minWidth: 0,
    minHeight: compact ? COMPACT_ACTION_MIN_HEIGHT : STACKED_ACTION_MIN_HEIGHT,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: 0,
    paddingRight: 0,
    gap: compact ? theme.spacing(0.75) : theme.spacing(0.25),
    color: confirmed ? theme.palette.success.dark : theme.palette.primary.main,
    borderRadius: 0,
    flexDirection: compact ? "row" : "column",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: confirmed ? "default" : "pointer",
    "&:hover": {
      backgroundColor: confirmed ? "transparent" : theme.palette.action.hover,
    },
    "&.Mui-disabled": {
      color: confirmed ? theme.palette.success.dark : theme.palette.action.disabled,
      opacity: confirmed ? 1 : undefined,
    },
  })
);

export const ProjectCard = styled(Card)({
  width: "100%",
  minHeight: 230,
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  padding: 0,
  overflow: "hidden",
  "&:hover": {
    boxShadow: `0px 4px 6px ${APP_COLORS.greyLight6}`,
  },
});

export const ProjectCardHeader = styled(Grid)({
  width: "100%",
  padding: 16,
  borderBottom: `1px dashed ${APP_COLORS.borderLight4}`,
});

export const ProjectLogo = styled(Avatar)({
  width: 30,
  height: 30,
  background: `linear-gradient(180deg, ${APP_COLORS.textMuted6} 0%, ${APP_COLORS.greyLight6} 100%)`,
  flexShrink: 0,
});

export const DateIcon = styled(CalendarTodayIcon)({
  width: 18,
  height: 18,
  color: APP_COLORS.textLight2,
});

export const ProjectCardContent = styled(Stack)({
  alignItems: "flex-start",
  width: "100%",
  gap: 8,
  padding: "16px",
  paddingBottom: 12,
  flex: 1,
  minHeight: 0,
});

export const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "bgColor" && prop !== "textColor",
})<{
  bgColor?: string;
  textColor?: string;
}>(({ bgColor, textColor }) => {
  const accentColor = textColor || APP_COLORS.success;
  const backgroundColor = bgColor || APP_COLORS.successDark4;

  return {
    height: "auto",
    minHeight: 24,
    maxWidth: "100%",
    borderRadius: 0,
    boxShadow: "none",
    padding: "5px 12px",
    paddingLeft: 10,
    width: "fit-content",
    backgroundColor,
    borderLeft: `3px solid ${accentColor}`,
    overflow: "visible",
    "& .MuiChip-label": {
      padding: 0,
      color: accentColor,
      textTransform: "uppercase",
      fontWeight: 700,
      letterSpacing: "0.08em",
      lineHeight: 1.25,
      fontSize: 11,
      whiteSpace: "normal",
      overflow: "visible",
      textOverflow: "unset",
    },
  };
});
